import {
  Directive,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  ElementRef,
  Input,
  HostBinding,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { Router } from '@angular/router';
import { EnvironmentPlatform } from 'src/environments/models/environment-platform';
import { NativeEnvironmentPlatform } from 'src/environments/models/native-environment-platform';

/**
 * Example: *appFlagApplication="application.All; page: '/edit', mode:'include'"
 */
@Directive({
  selector: '[appFlagApplication]',
})
export class FlagApplicationDirective implements OnInit {
  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private router: Router,
  ) {}

  @Input()
  public get appFlagApplication() {
    return this._appFlagApplication;
  }
  public set appFlagApplication(value: EnvironmentApplication | EnvironmentApplication[]) {
    this._appFlagApplication = value;
  }
  private _appFlagApplication: EnvironmentApplication | EnvironmentApplication[];

  @Input()
  public get appFlagApplicationMode(): 'include' | 'exclude' {
    return this._appFlagApplicationMode;
  }
  public set appFlagApplicationMode(value: 'include' | 'exclude') {
    this._appFlagApplicationMode = value;
  }
  private _appFlagApplicationMode: 'include' | 'exclude' = 'include';

  @Input()
  public get appFlagApplicationPage(): string | string[] {
    return this._appFlagApplicationPage;
  }
  public set appFlagApplicationPage(value: string | string[]) {
    this._appFlagApplicationPage = value;
  }
  private _appFlagApplicationPage: string | string[];

  @Input()
  public get appFlagApplicationPlatform(): EnvironmentPlatform | EnvironmentPlatform[] {
    return this._appFlagApplicationPlatform;
  }
  public set appFlagApplicationPlatform(value: EnvironmentPlatform | EnvironmentPlatform[]) {
    this._appFlagApplicationPlatform = value;
  }
  private _appFlagApplicationPlatform: EnvironmentPlatform | EnvironmentPlatform[];

  @Input()
  public get appFlagApplicationNative(): NativeEnvironmentPlatform | NativeEnvironmentPlatform[] {
    return this._appFlagApplicationNative;
  }
  public set appFlagApplicationNative(
    value: NativeEnvironmentPlatform | NativeEnvironmentPlatform[],
  ) {
    this._appFlagApplicationNative = value;
  }
  private _appFlagApplicationNative: NativeEnvironmentPlatform | NativeEnvironmentPlatform[];

  ngOnInit() {
    this.update();
  }

  update() {
    let hasMatch = false;
    let appMatch = false;
    let pageMatch = false;
    let platformMatch = false;
    let nativeMatch = false;

    // Determine if the current environment matches the flag
    if (this.appFlagApplication === '') {
      appMatch = true; // application.All is an empty string
    } else {
      if (Array.isArray(this.appFlagApplication)) {
        appMatch = this.appFlagApplication.some(
          (application) => application === environment.application,
        );
      } else if (typeof this.appFlagApplication === 'string') {
        appMatch = this.appFlagApplication === environment.application;
      }
    }

    // Page is optional so consider true when undeclared.
    if (this.appFlagApplicationPage == null) {
      pageMatch = true;
    } else {
      // When page is passed, determine if current page matches
      const url = this.router.url;

      if (Array.isArray(this.appFlagApplicationPage)) {
        pageMatch = this.appFlagApplicationPage.some((page) => url.indexOf(page) >= 0);
      } else if (typeof this.appFlagApplicationPage === 'string') {
        pageMatch = url.indexOf(this.appFlagApplicationPage) >= 0;
      }
    }

    // Platform is optional so consider true when undeclared.
    if (this.appFlagApplicationPlatform == null) {
      platformMatch = true;
    } else {
      // When page is passed, determine if current page matches
      const environmentPlatform = environment.platform;

      if (Array.isArray(this.appFlagApplicationPlatform)) {
        platformMatch = this.appFlagApplicationPlatform.some(
          (platform) => environmentPlatform.indexOf(platform) >= 0,
        );
      } else if (typeof this.appFlagApplicationPlatform === 'string') {
        platformMatch = environmentPlatform.indexOf(this.appFlagApplicationPlatform) >= 0;
      }
    }

    // native application check
    if (this.appFlagApplicationNative == null) {
      nativeMatch = true;
    } else {
      // default native match
      nativeMatch = environment.native;
      const nativePlatforms = Array.isArray(this.appFlagApplicationNative)
        ? this.appFlagApplicationNative
        : [this.appFlagApplicationNative];

      if (nativePlatforms.includes(NativeEnvironmentPlatform.iOS)) {
        // detect native iOS webview
        nativeMatch = environment.native && window['webkit'] !== undefined;
      }
    }

    hasMatch = appMatch && pageMatch && platformMatch && nativeMatch;

    // Hide or show depending on exclusion mode
    const include =
      (hasMatch && this.appFlagApplicationMode === 'include') ||
      (!hasMatch && this.appFlagApplicationMode === 'exclude');

    if (include) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else {
      this.viewContainer.clear();
    }
  }
}
